import React, { useState, useCallback } from 'react';
import {
  Box,
  TextField,
  Button,
  CircularProgress,
  Typography,
  Alert,
  Divider,
  alpha,
  useTheme,
} from '@mui/material';
import {
  AutoAwesome as AutoAwesomeIcon,
  Check as CheckIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import { analyzeApi } from '../../services/api';
import { trackThumbnailGenerated, trackThumbnailApplied } from '../../services/analytics';

/**
 * AiThumbnailGenerator — Generate thumbnails with Gemini Imagen 3.
 *
 * Props:
 *   platform         — 'youtube' | 'instagram_reel' (determines aspect ratio)
 *   onThumbnailReady — (file: File) => void — called when user confirms the generated image
 */
export default function AiThumbnailGenerator({ platform = 'youtube', onThumbnailReady }) {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  const [prompt, setPrompt] = useState('');
  const [loading, setLoading] = useState(false);
  const [applying, setApplying] = useState(false);
  const [generatedUrl, setGeneratedUrl] = useState(null);
  const [error, setError] = useState(null);
  const [applied, setApplied] = useState(false);

  const handleGenerate = useCallback(async () => {
    if (!prompt.trim()) {
      setError('Describe the thumbnail you want to generate.');
      return;
    }

    setError(null);
    setLoading(true);
    setGeneratedUrl(null);
    setApplied(false);
    setApplying(false);

    try {
      const data = await analyzeApi.generateThumbnail(prompt.trim(), platform);
      if (data?.url) {
        setGeneratedUrl(data.url);
        trackThumbnailGenerated(platform);
      } else {
        setError('No image was returned. Try a different prompt.');
      }
    } catch (err) {
      let msg;
      if (err?.isUpgradeRequired) {
        msg = 'AI thumbnail generation is available on Creator plan and above. Upgrade to unlock this feature.';
      } else if (err?.errorType === 'billing') {
        msg = 'AI image generation requires a paid Google AI plan. Please contact support to enable this feature.';
      } else if (err?.errorType === 'quota' || err?.isQuotaError) {
        msg = 'Image generation quota exhausted. Please try again later or contact support.';
      } else {
        msg = err?.message || 'Thumbnail generation failed. Please try again.';
      }
      setError(msg);
    } finally {
      setLoading(false);
    }
  }, [prompt, platform]);

  const handleUseThisThumbnail = useCallback(async () => {
    if (!generatedUrl || applying) return;

    setApplying(true);
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 15_000); // 15s cap

    try {
      const response = await fetch(generatedUrl, { // foxguard: ignore[js/no-ssrf]
        mode: 'cors',
        credentials: 'omit',
        signal: controller.signal,
      });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const blob = await response.blob();
      const file = new File([blob], 'ai-thumbnail.jpg', { type: 'image/jpeg' });
      onThumbnailReady(file);
      trackThumbnailApplied(platform);
      setApplied(true);
    } catch (err) {
      const msg = err?.name === 'AbortError'
        ? 'Image fetch timed out. Please regenerate.'
        : 'Failed to load the generated image. Please regenerate.';
      setError(msg);
    } finally {
      clearTimeout(timeoutId);
      setApplying(false);
    }
  }, [generatedUrl, applying, onThumbnailReady, platform]);

  return (
    <Box sx={{ mt: 2 }}>
      <Divider sx={{ mb: 2 }}>
        <Typography variant="caption" sx={{ color: 'text.disabled', px: 1 }}>
          or generate with AI
        </Typography>
      </Divider>

      <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
        <TextField
          fullWidth
          size="small"
          placeholder="Describe your thumbnail... e.g. &quot;dramatic sunset with a person silhouette&quot;"
          value={prompt}
          onChange={(e) => {
            setPrompt(e.target.value);
            if (applied) setApplied(false);
          }}
          disabled={loading}
          sx={{
            '& .MuiOutlinedInput-root': {
              borderRadius: '6px',
              fontSize: '0.85rem',
            },
          }}
        />
        <Button
          variant="contained"
          onClick={handleGenerate}
          disabled={loading || !prompt.trim()}
          size="small"
          startIcon={
            loading ? (
              <CircularProgress size={16} color="inherit" />
            ) : (
              <AutoAwesomeIcon sx={{ fontSize: 16 }} />
            )
          }
          sx={{
            minWidth: 120,
            borderRadius: '6px',
            fontWeight: 700,
            textTransform: 'none',
            whiteSpace: 'nowrap',
            background: 'linear-gradient(135deg, #6366f1, #8b5cf6)',
            '&:hover': {
              background: 'linear-gradient(135deg, #4f46e5, #7c3aed)',
            },
            '&.Mui-disabled': {
              background: alpha('#6366f1', 0.3),
              color: alpha('#fff', 0.5),
            },
          }}
        >
          {loading ? 'Generating...' : generatedUrl ? 'Regenerate' : 'Generate'}
        </Button>
      </Box>

      {error && (
        <Alert
          severity={
            error.includes('Upgrade') || error.includes('upgrade')
              ? 'info'
              : error.includes('quota') || error.includes('plan') || error.includes('billing')
              ? 'warning'
              : 'error'
          }
          onClose={() => setError(null)}
          sx={{ mt: 1.5, borderRadius: '6px', fontSize: '0.8rem' }}
        >
          {error}
        </Alert>
      )}

      {generatedUrl && (
        <Box
          sx={{
            mt: 2,
            borderRadius: '8px',
            border: `1px solid ${alpha(isDark ? '#818cf8' : '#6366f1', 0.3)}`,
            overflow: 'hidden',
            background: isDark ? alpha('#000', 0.2) : alpha('#f8f9fa', 0.8),
          }}
        >
          <Box
            component="img"
            src={generatedUrl}
            alt="AI-generated thumbnail"
            onError={() => {
              setError('Preview failed to load — the image may have expired. Please regenerate.');
              setGeneratedUrl(null);
            }}
            sx={{
              width: '100%',
              display: 'block',
              maxHeight: 300,
              objectFit: 'contain',
              background: '#000',
            }}
          />
          <Box
            sx={{
              display: 'flex',
              gap: 1,
              p: 1.5,
              justifyContent: 'flex-end',
            }}
          >
            <Button
              size="small"
              variant="outlined"
              startIcon={<RefreshIcon sx={{ fontSize: 16 }} />}
              onClick={handleGenerate}
              disabled={loading}
              sx={{
                borderRadius: '6px',
                textTransform: 'none',
                fontWeight: 600,
                fontSize: '0.8rem',
              }}
            >
              Regenerate
            </Button>
            <Button
              size="small"
              variant="contained"
              startIcon={
                applying ? (
                  <CircularProgress size={16} color="inherit" />
                ) : (
                  <CheckIcon sx={{ fontSize: 16 }} />
                )
              }
              onClick={handleUseThisThumbnail}
              disabled={applied || applying}
              sx={{
                borderRadius: '6px',
                textTransform: 'none',
                fontWeight: 700,
                fontSize: '0.8rem',
                background: applied
                  ? theme.palette.success.main
                  : 'linear-gradient(135deg, #6366f1, #8b5cf6)',
                '&:hover': {
                  background: applied
                    ? theme.palette.success.dark
                    : 'linear-gradient(135deg, #4f46e5, #7c3aed)',
                },
              }}
            >
              {applied ? 'Applied' : applying ? 'Applying...' : 'Use This Thumbnail'}
            </Button>
          </Box>
        </Box>
      )}
    </Box>
  );
}
